import React, { useEffect, useState } from 'react';
import { ActiveUser } from '../../hooks/useCollaboration';

interface CollaborativeCursorsOverlayProps {
  activeUsers: ActiveUser[];
  currentUserId: string;
  containerRef: React.RefObject<HTMLDivElement>;
  scale: number;
}

/**
 * Overlay transparent positionné sur le canvas swimlane.
 * Rend le curseur + nom de chaque participant actif (sauf soi-même).
 *
 * Système de coordonnées :
 *   - Les positions reçues sont en "coordonnées logiques du canvas"
 *     (i.e. pixel / scale sur l'émetteur, relatif au coin haut-gauche
 *     de containerRef).
 *   - Pour les afficher dans l'overlay (absolute inset-0 dans parentRef),
 *     on ajoute l'offset de containerRef dans parentRef (ex. marginTop
 *     du toolbar en mode édition).
 */
const CollaborativeCursorsOverlay: React.FC<CollaborativeCursorsOverlayProps> = ({
  activeUsers,
  currentUserId,
  containerRef,
  scale,
}) => {
  // Offset de containerRef par rapport à son parent (parentRef).
  // Inclut le marginTop lorsque le mode édition est actif.
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const updateOffset = () => {
      if (!containerRef.current) return;
      const parent = containerRef.current.parentElement;
      if (!parent) return;
      // getBoundingClientRect() sur containerRef scalé : left/top restent
      // ceux du coin haut-gauche d'origine (transformOrigin: top left).
      const containerRect = containerRef.current.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      setOffset({
        x: containerRect.left - parentRect.left,
        y: containerRect.top - parentRect.top,
      });
    };

    updateOffset();

    // Recalcule si containerRef change de taille (ex. changement de marginTop)
    const ro = new ResizeObserver(updateOffset);
    if (containerRef.current) ro.observe(containerRef.current);

    // Recalcule aussi si le style de containerRef est muté (marginTop via JS)
    const mo = new MutationObserver(updateOffset);
    if (containerRef.current) {
      mo.observe(containerRef.current, { attributes: true, attributeFilter: ['style'] });
    }

    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [containerRef]);

  const otherUsers = activeUsers.filter(
    u => u.user_id !== currentUserId && u.cursor_position != null
  );

  if (otherUsers.length === 0) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 500 }}
    >
      {otherUsers.map(user => {
        const pos = user.cursor_position!;
        // Position logique → position écran dans l'overlay :
        //   pos.x/y sont en coordonnées canvas (avant scale, relatives à containerRef)
        //   → on multiplie par scale pour retrouver les pixels écran
        //   → on ajoute l'offset de containerRef dans parentRef (ex. marginTop)
        const x = pos.x * scale + offset.x;
        const y = pos.y * scale + offset.y;

        return (
          <div
            key={user.user_id}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              transform: 'translate(0, 0)',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {/* Icône curseur SVG colorée */}
            <svg
              width="20"
              height="24"
              viewBox="0 0 20 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
            >
              <path
                d="M0 0L0 20L5.5 14.5L9 22L11.5 21L8 13.5H14.5L0 0Z"
                fill={user.color}
              />
              <path
                d="M0 0L0 20L5.5 14.5L9 22L11.5 21L8 13.5H14.5L0 0Z"
                stroke="white"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>

            {/* Bulle nom */}
            <div
              style={{
                position: 'absolute',
                top: 18,
                left: 14,
                backgroundColor: user.color,
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 7px',
                borderRadius: '0 8px 8px 8px',
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                lineHeight: 1.4,
                maxWidth: 140,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user.fullname.split(' ')[0]}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CollaborativeCursorsOverlay;
